import { useNavigate } from 'react-router-dom';
import {
  useReturnRequisition,
  useSubmitForOrderingAdminApproval,
  useSubmitForZonalApproval,
  useSubmitLeasingReqToGSA,
  useSubmitLeasingAreq,
  useSubmitToFMCApproval,
  useReturnRequisitionToStatus,
} from '../requests/leasing';
import { useCurrentUser, useAppAbility } from '@gsa/afp-shared-ui-utils';
import { useSelector, useDispatch } from 'react-redux';
import { RequisitionStatus } from '../pages/ReviewDetails/RequisitionDetailsUtils';
import { RequisitionViewMode } from '../pages/leasing/leasing-consts';
import {
  setRequisitionCartValidations,
  setSubmitButtonDisabled,
} from '../reducers/leasing';
import { UserRoles } from '../constants/index.jsx';
import { useMemo } from 'react';
import { StoreOperations, StoreSubjects } from '../constants/constants.jsx';
import { REQUISITION_TYPE } from '../pages/non-standard-purchase/constants';

export default function useSubmitLeasingRequisition({
  submitRequisitionModal,
  requisitionValidationModal,
} = {}) {
  const { requisitionId, requisitionStatus, requisitionNumber, isAreq } =
    useSelector((state) => state.leasingReducer);
  const dispatch = useDispatch();
  const submitToFMCApproval = useSubmitToFMCApproval();
  const submitForZonalApproval = useSubmitForZonalApproval();
  const submitLeasingReqToGSA = useSubmitLeasingReqToGSA();
  const submitLeasingAreq = useSubmitLeasingAreq();
  const returnRequisition = useReturnRequisition();
  const returnRequisitionToStatus = useReturnRequisitionToStatus();
  const submitForOrderingAdminApproval = useSubmitForOrderingAdminApproval();
  const navigate = useNavigate();

  const { currentUser } = useCurrentUser();
  const ability = useAppAbility();

  const admin = currentUser?.roles?.some((role) => role?.id === '100');
  const fsr = currentUser?.roles?.some((role) => role?.id === '1');
  const fmcManager = currentUser?.roles?.some((role) => role?.id === '2001');
  const orderingAdmin = currentUser?.roles?.some((role) => role?.id === '1503');
  const zonalAcquisitionCoordinator = currentUser?.roles?.some(
    (role) => role?.id === '1504',
  );
  const validLeasingUsers = admin || fsr || fmcManager || orderingAdmin;
  const isContractingOfficer = currentUser?.roles?.some(
    (role) => role.name === UserRoles.CONTRACTING_OFFICER,
  );
  const isEngineer = useMemo(
    () => ability?.can(StoreOperations.Create, StoreSubjects.CatalogCodes),
    [ability],
  );

  /** BEGIN SUBMIT METHOD */
  let submitMethod = () => {};
  if (
    requisitionStatus === RequisitionStatus.DRAFT ||
    requisitionStatus === RequisitionStatus.RETURNED ||
    requisitionStatus === RequisitionStatus.ENGINEERING_REVIEW ||
    requisitionStatus === RequisitionStatus.CONTRACTING_REVIEW ||
    requisitionStatus === RequisitionStatus.ENGINEERING_APPROVAL ||
    requisitionStatus === RequisitionStatus.CONTRACTING_APPROVAL
  ) {
    if (isAreq) {
      submitMethod = submitLeasingAreq;
    } else if (fsr && !fmcManager) {
      submitMethod = submitToFMCApproval;
    } else if (fmcManager) {
      submitMethod = submitForZonalApproval;
    } else if (zonalAcquisitionCoordinator) {
      submitMethod = submitForOrderingAdminApproval;
    } else if (orderingAdmin || admin) {
      submitMethod = submitLeasingReqToGSA;
    }
  } else if (
    requisitionStatus === RequisitionStatus.FMC_APPROVAL &&
    (fmcManager || fsr || admin)
  ) {
    submitMethod = submitForZonalApproval;
  } else if (
    requisitionStatus === RequisitionStatus.ZONAL_APPROVAL &&
    (zonalAcquisitionCoordinator || admin)
  ) {
    submitMethod = submitForOrderingAdminApproval;
  } else if (
    requisitionStatus === RequisitionStatus.ORDERING_ADMIN_APPROVAL &&
    (orderingAdmin || admin)
  ) {
    submitMethod = submitLeasingReqToGSA;
  }
  /** END SUBMIT METHOD */

  /** BEGIN RETURN METHOD */
  let returnMethod = () => {};
  if (
    (requisitionStatus === RequisitionStatus.FMC_APPROVAL &&
      (fmcManager || fsr || admin)) ||
    (requisitionStatus === RequisitionStatus.ZONAL_APPROVAL &&
      (zonalAcquisitionCoordinator || admin)) ||
    (requisitionStatus === RequisitionStatus.ORDERING_ADMIN_APPROVAL &&
      (orderingAdmin || admin) &&
      !isAreq)
  ) {
    returnMethod = returnRequisition;
  }
  if (
    isAreq &&
    ([
      RequisitionStatus.CONTRACTING_REVIEW,
      RequisitionStatus.ENGINEERING_REVIEW,
      RequisitionStatus.ENGINEERING_APPROVAL,
      RequisitionStatus.CONTRACTING_APPROVAL,
    ].includes(requisitionStatus) ||
      ((orderingAdmin || admin) &&
        requisitionStatus === RequisitionStatus.ORDERING_ADMIN_APPROVAL))
  ) {
    returnMethod = returnRequisitionToStatus;
  }
  /** END RETURN METHOD */

  /** BEGIN SUBMIT LABEL */
  let submitLabel;
  if (
    requisitionStatus === RequisitionStatus.DRAFT ||
    requisitionStatus === RequisitionStatus.RETURNED ||
    requisitionStatus === RequisitionStatus.ENGINEERING_REVIEW ||
    requisitionStatus === RequisitionStatus.CONTRACTING_REVIEW ||
    requisitionStatus === RequisitionStatus.ENGINEERING_APPROVAL ||
    requisitionStatus === RequisitionStatus.CONTRACTING_APPROVAL
  ) {
    if (isAreq) {
      if (
        requisitionStatus === RequisitionStatus.DRAFT ||
        requisitionStatus === RequisitionStatus.RETURNED
      ) {
        submitLabel = 'Submit for engineer review';
      } else if (requisitionStatus === RequisitionStatus.ENGINEERING_REVIEW) {
        submitLabel = 'Submit for contracting review';
      } else if (requisitionStatus === RequisitionStatus.CONTRACTING_REVIEW) {
        submitLabel = 'Submit to engineering approval';
      } else if (requisitionStatus === RequisitionStatus.ENGINEERING_APPROVAL) {
        submitLabel = 'Approve and submit to contracting approval';
      } else if (requisitionStatus === RequisitionStatus.CONTRACTING_APPROVAL) {
        submitLabel = 'Approve and submit to ordering admin';
      }
    } else if (fsr && !fmcManager) {
      submitLabel = 'Submit to FMC approval';
    } else if (fmcManager) {
      submitLabel = 'Submit to zonal approval';
    } else if (zonalAcquisitionCoordinator) {
      submitLabel = 'Submit to ordering management approver';
    } else if (orderingAdmin || admin) {
      submitLabel = 'Place order';
    }
  } else if (
    requisitionStatus === RequisitionStatus.FMC_APPROVAL &&
    (fmcManager || fsr || admin)
  ) {
    submitLabel = 'Submit to zonal approval';
  } else if (
    requisitionStatus === RequisitionStatus.ZONAL_APPROVAL &&
    (zonalAcquisitionCoordinator || admin)
  ) {
    submitLabel = 'Submit to ordering management approver';
  } else if (
    requisitionStatus === RequisitionStatus.ORDERING_ADMIN_APPROVAL &&
    (orderingAdmin || admin)
  ) {
    submitLabel = 'Place order';
  }
  /** END SUBMIT LABEL */

  const determineRequisitionViewMode = (requisition) => {
    if (requisition?.requisitionStatus === RequisitionStatus.RECEIVED_BY_GSA) {
      return RequisitionViewMode.READONLY;
    }

    if (requisition?.requisitionStatus === RequisitionStatus.FMC_APPROVAL) {
      if (fmcManager || fsr || admin) {
        return RequisitionViewMode.REVIEW;
      } else {
        return RequisitionViewMode.READONLY;
      }
    }

    if (requisition?.requisitionStatus === RequisitionStatus.ZONAL_APPROVAL) {
      if (zonalAcquisitionCoordinator || admin) {
        return RequisitionViewMode.REVIEW;
      } else {
        return RequisitionViewMode.READONLY;
      }
    }

    if (
      requisition?.requisitionStatus ===
      RequisitionStatus.ORDERING_ADMIN_APPROVAL
    ) {
      if (orderingAdmin || admin) {
        return RequisitionViewMode.REVIEW;
      } else {
        return RequisitionViewMode.READONLY;
      }
    }

    if (requisition?.requisitionType === REQUISITION_TYPE.AREQ) {
      if (
        (requisition?.requisitionStatus === RequisitionStatus.DRAFT ||
          requisition?.requisitionStatus === RequisitionStatus.RETURNED) &&
        (validLeasingUsers || requisition?.createdBy === currentUser?.id)
      ) {
        return RequisitionViewMode.EDIT;
      }
      if (
        requisition?.requisitionStatus ===
        RequisitionStatus.ORDERING_ADMIN_APPROVAL
      ) {
        if (orderingAdmin || admin) {
          return RequisitionViewMode.REVIEW;
        } else {
          return RequisitionViewMode.READONLY;
        }
      }
      if (
        requisition?.requisitionStatus === RequisitionStatus.CONTRACTING_REVIEW
      ) {
        if (isContractingOfficer || admin || orderingAdmin) {
          return RequisitionViewMode.REVIEW;
        } else {
          return RequisitionViewMode.READONLY;
        }
      }
      if (
        requisition?.requisitionStatus ===
        RequisitionStatus.ENGINEERING_APPROVAL
      ) {
        if (isEngineer || isContractingOfficer || orderingAdmin || admin) {
          return RequisitionViewMode.REVIEW;
        } else {
          return RequisitionViewMode.READONLY;
        }
      }
      if (
        requisition?.requisitionStatus ===
        RequisitionStatus.CONTRACTING_APPROVAL
      ) {
        if (orderingAdmin || admin || isContractingOfficer) {
          return RequisitionViewMode.REVIEW;
        } else {
          return RequisitionViewMode.READONLY;
        }
      }
      if (
        requisition?.requisitionStatus === RequisitionStatus.ENGINEERING_REVIEW
      ) {
        if (requisition?.assignedEngineer?.id === currentUser?.id) {
          return RequisitionViewMode.REVIEW;
        } else {
          return RequisitionViewMode.READONLY;
        }
      }
    }

    if (
      validLeasingUsers &&
      (!requisition ||
        requisition?.requisitionStatus === RequisitionStatus.DRAFT ||
        requisition?.requisitionStatus === RequisitionStatus.RETURNED)
    ) {
      return RequisitionViewMode.EDIT;
    } else {
      return RequisitionViewMode.READONLY;
    }
  };

  const handleSubmit = ({ variables }) => {
    return new Promise((resolve, reject) => {
      if (
        (requisitionStatus === RequisitionStatus.FMC_APPROVAL &&
          (fmcManager || fsr || admin)) ||
        (requisitionStatus === RequisitionStatus.ZONAL_APPROVAL &&
          (zonalAcquisitionCoordinator || admin)) ||
        (requisitionStatus === RequisitionStatus.ORDERING_ADMIN_APPROVAL &&
          (orderingAdmin || admin))
      ) {
        submitRequisitionModal.openModal();
        resolve();
      } else {
        dispatch(setSubmitButtonDisabled(true));
        const [submitRequisition] = submitMethod;

        submitRequisition({
          variables: {
            requisitionId,
            ...variables,
          },
        })
          .then((res) => {
            const requisitionCartValidations =
              res?.data?.requisitionCartValidations;
            if (
              !res?.data?.operationStatus &&
              requisitionCartValidations &&
              requisitionCartValidations?.validationErrors?.length > 0
            ) {
              dispatch(
                setRequisitionCartValidations(requisitionCartValidations),
              );
              requisitionValidationModal.openModal();
            } else {
              navigate(
                `/my-requisitions?filter-requisitionNumber=${encodeURIComponent(
                  requisitionNumber,
                )}`,
              );
              dispatch(setSubmitButtonDisabled(false));
            }
            resolve();
          })
          .catch((err) => {
            dispatch(setSubmitButtonDisabled(false));
            console.error(err);
            reject(err);
          });
      }
    });
  };

  return {
    handleLeasingRequisitionSubmit: handleSubmit,
    leasingRequisitionSubmitMethod: submitMethod,
    leasingRequisitionReturnMethod: returnMethod,
    determineRequisitionViewMode,
    leasingRequisitionSubmitLabel: submitLabel,
  };
}
